import { cn } from "@/utils/utils";

const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

/**
 * "September" or "September 2025" — month label for a "YYYY-MM-DD" period
 * string. Parsed from the string's components directly (never through `Date` +
 * timezone conversion) so a first-of-month value can never be shifted to an
 * adjacent date by the reader's local timezone.
 */
function monthLabel(period: string, currentYear: number): string {
    const [yearStr = "", monthStr = ""] = period.split("-");
    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1;
    const name = MONTH_NAMES[monthIndex] ?? monthStr;
    return year === currentYear ? name : `${name} ${year}`;
}

export interface TMonthCheckboxGroupProps {
    /** Up to the 4 months before the current one, oldest first (board.availablePeriods). */
    availablePeriods: string[];
    /** Which of the available periods are checked (board.selectedPeriods). */
    selectedPeriods: string[];
    onChange: (periods: string[]) => void;
    /** Used to decide whether a period's year label is shown. */
    currentYear?: number;
    className?: string;
}

/**
 * Month period selector for the Homepage v2 board (design doc §7.1).
 * Multi-select, up to the 4 months before the current one. At least one month must stay
 * checked — unchecking the last one is a no-op rather than sending an
 * empty selection the board would have to default on its own.
 *
 * Rendered as toggle pills in the same tray as `SegmentedToggle`, so it sits
 * alongside the Type / Board view toggles as one family — but with a solid
 * selected state, since several months can be on at once. Pills rather than
 * native checkboxes: each pill is a full
 * hit target, which matters for a control clicked as often as this one, and a
 * solid/outline pair reads as "which months the board is showing" far more
 * directly than four small boxes did. An unselected pill is prefixed `+` —
 * the months read as a stack you add to, starting from the most recent. The
 * `+` is decorative (`aria-hidden`), so the accessible name stays the plain
 * month. The native input is kept — transparent, stretched across the pill —
 * so this stays a real multi-select checkbox group for keyboard and
 * screen-reader users.
 *
 * Newest first, unlike `availablePeriods`: the most recent month is the one
 * selected by default and the one reached for most, so it leads.
 */
export function MonthCheckboxGroup({
    availablePeriods,
    selectedPeriods,
    onChange,
    currentYear = new Date().getFullYear(),
    className,
}: TMonthCheckboxGroupProps) {
    const selectedSet = new Set(selectedPeriods);

    const toggle = (period: string, checked: boolean) => {
        if (!checked && selectedSet.size <= 1 && selectedSet.has(period)) {
            return; // never uncheck the last remaining month
        }
        const next = checked
            ? [...selectedPeriods, period]
            : selectedPeriods.filter((p) => p !== period);
        onChange([...next].sort());
    };

    return (
        <div
            role="group"
            aria-label="Select months"
            className={cn(
                "inline-flex items-center gap-1 rounded-xl border bg-muted/40 p-1",
                className,
            )}
        >
            {[...availablePeriods].reverse().map((period) => {
                const checked = selectedSet.has(period);
                const label = monthLabel(period, currentYear);
                return (
                    <label
                        key={period}
                        className={cn(
                            "relative flex cursor-pointer select-none items-center gap-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                            checked
                                ? "bg-foreground text-background shadow-proda-sm"
                                : "text-muted-foreground hover:bg-card hover:text-foreground",
                        )}
                    >
                        <input
                            type="checkbox"
                            // Named explicitly so the decorative `+` never
                            // becomes part of what is announced.
                            aria-label={label}
                            // Keyed by the period itself: the visible label
                            // is month-name-and-year-dependent, which makes it
                            // a poor test target.
                            data-testid={`month-checkbox-${period}`}
                            checked={checked}
                            onChange={(e) => toggle(period, e.target.checked)}
                            // A transparent input stretched over the whole
                            // pill rather than a hidden one: the entire pill
                            // is then the real hit target for a pointer, a
                            // headless browser and assistive tech alike.
                            className="absolute inset-0 h-full w-full cursor-pointer appearance-none rounded-lg opacity-0"
                        />
                        {!checked && <span aria-hidden="true">+</span>}
                        <span aria-hidden="true">{label}</span>
                    </label>
                );
            })}
        </div>
    );
}
